/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, {
  FunctionComponent,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import Alert from 'src/components/Alert';
import { SupersetClient, SupersetTheme, t, styled } from '@superset-ui/core';
import TableView, { EmptyWrapperType } from 'src/components/TableView';
import { ServerPagination, SortByType } from 'src/components/TableView/types';
import StyledModal from 'src/components/Modal';
import Button from 'src/components/Button';
import { useListViewResource } from 'src/views/CRUD/hooks';
import Dataset from 'src/types/Dataset';
import { useDebouncedEffect } from 'src/explore/exploreUtils';
import { SLOW_DEBOUNCE } from 'src/constants';
import { getClientErrorObject } from 'src/utils/getClientErrorObject';
import Loading from 'src/components/Loading';
import { AntdInput } from 'src/components';
import {
  PAGE_SIZE as DATASET_PAGE_SIZE,
  SORT_BY as DATASET_SORT_BY,
} from 'src/features/datasets/constants';
import withToasts from 'src/components/MessageToasts/withToasts';
import DatasourceCell from 'src/components/DatasourceCell';
import PanelSearchInput from 'src/components/PanelSearchInput';
import DatabaseAsyncSelect, {
  DatabaseSelectValue,
  toDatabaseSelectValue,
} from 'src/components/DatabaseSelector/DatabaseAsyncSelect';
import { DatabaseObject } from 'src/components/DatabaseSelector';
import FacePile from '../FacePile';

const CONFIRM_WARNING_MESSAGE = t(
  'Warning! Changing the dataset may break the chart if the metadata does not exist.',
);

const CHANGE_WARNING_MSG = t(
  'Changing the dataset may break the chart if the chart relies ' +
    'on columns or metadata that does not exist in the target dataset',
);

interface Datasource {
  type: string;
  id: number;
  uid: string;
}

interface ChangeDatasourceModalProps {
  addDangerToast: (msg: string) => void;
  addSuccessToast: (msg: string) => void;
  defaultDatabase?: DatabaseObject | null;
  onChange: (uid: string) => void;
  onDatasourceSave: (datasource: object, errors?: Array<any>) => {};
  onHide: () => void;
  show: boolean;
}

const Modal = styled(StyledModal)`
  .ant-modal-body {
    display: flex;
    flex-direction: column;
  }

  .table-cell {
    font-size: 14px;
    line-height: 1.35;
  }

  .table tbody td {
    font-size: 14px;
    line-height: 1.35;
  }
`;

const ConfirmModalStyled = styled.div`
  .btn-container {
    display: flex;
    justify-content: flex-end;
    padding: 0px 15px;
    margin: 10px 0 0 0;
  }

  .confirm-modal-container {
    margin: 9px;
  }
`;

const FilterRow = styled.div`
  ${({ theme }) => `
    display: flex;
    gap: ${theme.gridUnit * 2}px;
    margin-bottom: ${theme.gridUnit * 3}px;
    align-items: center;

    .database-select {
      flex: 0 0 260px;
    }

    .dataset-search {
      flex: 1;
      min-width: 0;
    }
  `}
`;

const DatasetSelectTrigger = styled.button`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  display: block;
  width: 100%;
  text-align: left;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const ChangeDatasourceModal: FunctionComponent<ChangeDatasourceModalProps> = ({
  addDangerToast,
  addSuccessToast,
  defaultDatabase,
  onChange,
  onDatasourceSave,
  onHide,
  show,
}) => {
  const [filter, setFilter] = useState<any>(undefined);
  const [selectedDatabase, setSelectedDatabase] =
    useState<DatabaseSelectValue | null>(null);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [sortBy, setSortBy] = useState<SortByType>(DATASET_SORT_BY);
  const [confirmChange, setConfirmChange] = useState(false);
  const [confirmedDataset, setConfirmedDataset] = useState<Datasource>();
  const searchRef = useRef<AntdInput>(null);

  const {
    state: { loading, resourceCollection, resourceCount },
    fetchData,
  } = useListViewResource<Dataset>('dataset', t('dataset'), addDangerToast);

  const selectDatasource = useCallback((datasource: Datasource) => {
    setConfirmChange(true);
    setConfirmedDataset(datasource);
  }, []);

  const fetchDatasetPayload = {
    pageIndex,
    pageSize: DATASET_PAGE_SIZE,
    filters: [],
    sortBy,
  };

  useDebouncedEffect(
    () => {
      const filters = [];
      if (selectedDatabase?.id) {
        filters.push({
          id: 'database',
          operator: 'rel_o_m',
          value: selectedDatabase.id,
        });
      }
      if (filter) {
        filters.push({
          id: 'table_name',
          operator: 'ct',
          value: filter,
        });
      }
      fetchData({
        ...fetchDatasetPayload,
        ...(filters.length > 0 && { filters }),
      });
    },
    SLOW_DEBOUNCE,
    [filter, pageIndex, selectedDatabase?.id, sortBy],
  );

  useEffect(() => {
    if (show) {
      setSelectedDatabase(
        defaultDatabase?.id ? toDatabaseSelectValue(defaultDatabase) : null,
      );
      setFilter(undefined);
      setPageIndex(0);
      setTimeout(() => searchRef?.current?.focus(), 200);
    }
  }, [defaultDatabase, show]);

  const changeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value ?? '';
    setFilter(searchValue);
    setPageIndex(0);
  };

  const changeDatabase = (database: DatabaseSelectValue | null) => {
    setSelectedDatabase(database);
    setPageIndex(0);
  };

  const handleChangeConfirm = () => {
    SupersetClient.get({
      endpoint: `/api/v1/dataset/${confirmedDataset?.id}`,
    })
      .then(({ json }) => {
        // eslint-disable-next-line no-param-reassign
        json.result.type = 'table';
        onDatasourceSave(json.result);
        onChange(`${confirmedDataset?.id}__table`);
      })
      .catch(response => {
        getClientErrorObject(response).then(
          ({ error, message }: { error: any; message: string }) => {
            const errorMessage = error
              ? error.error || error.statusText || error
              : message;
            addDangerToast(errorMessage);
          },
        );
      });
    onHide();
    addSuccessToast(t('Successfully changed dataset!'));
  };

  const handlerCancelConfirm = () => {
    setConfirmChange(false);
  };

  const columns = [
    {
      Cell: ({ row: { original } }: any) => (
        <DatasetSelectTrigger
          type="button"
          data-test="datasource-link"
          onClick={() => selectDatasource({ type: 'table', ...original })}
        >
          <DatasourceCell
            dataset={original.table_name}
            database={original.database?.database_name}
          />
        </DatasetSelectTrigger>
      ),
      Header: t('数据集名称'),
      accessor: 'table_name',
    },
    {
      Cell: ({ row: { original } }: any) => {
        const kind =
          original.kind === 'physical' ? t('physical') : t('virtual');
        const kindIcon =
          original.kind === 'physical' ? (
            <sup id="physical-counts">
              <svg
                className="physical-icon-counts"
                width="1em"
                height="1em"
                version="1.1"
                viewBox="0 0 1024 1024"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M725.333333 640h85.333334v85.333333h-85.333334z m-128 0h85.333334v85.333333h-85.333334z"
                  fill="currentColor"
                />
                <path
                  d="M937.386667 544.341333l-85.333334-341.333333A42.709333 42.709333 0 0 0 810.666667 170.666667H256c-17.792 0-33.706667 11.050667-39.978667 27.648l-128 341.333333 0.298667 0.128A42.282667 42.282667 0 0 0 85.333333 554.666667v256a42.666667 42.666667 0 0 0 42.666667 42.666666h768a42.666667 42.666667 0 0 0 42.666667-42.666666v-256c0-3.584-1.194667-6.784-2.048-10.154667l0.768-0.170667zM285.568 256h491.733333l64.042667 256H189.568l96-256zM853.333333 768H170.666667v-170.666667h682.666666v170.666667z"
                  fill="currentColor"
                />
              </svg>
              HD
            </sup>
          ) : (
            <sup id="virtual-counts">
              <svg
                className="virtual-icon-counts"
                width="1em"
                height="1em"
                version="1.1"
                viewBox="0 0 1024 1024"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M625.066667 625.066667a241.066667 241.066667 0 1 0-341.333334 0 241.066667 241.066667 0 0 0 341.333334 0z m12.8 125.866666a347.733333 347.733333 0 1 1 113.066666-477.866666 341.333333 341.333333 0 0 1 0 364.8l138.666667 138.666666a81.066667 81.066667 0 0 1-106.666667 119.466667l-6.4-6.4-138.666666-138.666667z"
                  fill="currentColor"
                />
              </svg>
              SQL
            </sup>
          );
        return (
          <span>
            {kind}
            {kindIcon}
          </span>
        );
      },
      Header: t('Type'),
      accessor: 'kind',
      disableSortBy: true,
    },
    // {
    //   Header: t('探索'),
    //   accessor: 'explore_url',
    //   Cell: ({ row: { original } }: any) => (
    //     <a
    //       href={original.explore_url}
    //       target="_blank"
    //       rel="noopener noreferrer"
    //     >
    //       {original.explore_url}
    //     </a>
    //   ),
    // },
    {
      Header: t('Schema'),
      accessor: 'schema',
    },
    {
      Cell: ({
        row: {
          original: { owners = [] },
        },
      }: any) => <FacePile users={owners} />,
      Header: t('Owners'),
      id: 'owners',
      disableSortBy: true,
    },
  ];

  const onServerPagination = (args: ServerPagination) => {
    setPageIndex(args.pageIndex);
    if (args.sortBy) {
      // ensure default sort by
      setSortBy(args.sortBy.length > 0 ? args.sortBy : DATASET_SORT_BY);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      responsive
      title={t('Swap dataset')}
      width={confirmChange ? '432px' : ''}
      height={confirmChange ? 'auto' : '540px'}
      hideFooter={!confirmChange}
      footer={
        <>
          {confirmChange && (
            <ConfirmModalStyled>
              <div className="btn-container">
                <Button onClick={handlerCancelConfirm}>{t('Cancel')}</Button>
                <Button
                  className="proceed-btn"
                  buttonStyle="primary"
                  onClick={handleChangeConfirm}
                >
                  {t('Proceed')}
                </Button>
              </div>
            </ConfirmModalStyled>
          )}
        </>
      }
    >
      <>
        {!confirmChange && (
          <>
            <Alert
              roomBelow
              type="warning"
              css={(theme: SupersetTheme) => ({
                marginBottom: theme.gridUnit * 4,
              })}
              message={
                <>
                  <strong>{t('Warning!')}</strong> {CHANGE_WARNING_MSG}
                </>
              }
            />
            <FilterRow>
              <div className="database-select">
                <DatabaseAsyncSelect
                  onChange={changeDatabase}
                  value={selectedDatabase}
                />
              </div>
              <PanelSearchInput
                ref={searchRef}
                className="dataset-search"
                value={filter ?? ''}
                placeholder={t('搜索数据集名称...')}
                onChange={changeSearch}
              />
            </FilterRow>
            {loading && <Loading />}
            {!loading && (
              <TableView
                columns={columns}
                data={resourceCollection}
                pageSize={DATASET_PAGE_SIZE}
                initialPageIndex={pageIndex}
                initialSortBy={sortBy}
                totalCount={resourceCount}
                onServerPagination={onServerPagination}
                className="table-condensed"
                emptyWrapperType={EmptyWrapperType.Small}
                serverPagination
                isPaginationSticky
                scrollTable
              />
            )}
          </>
        )}
        {confirmChange && <>{CONFIRM_WARNING_MESSAGE}</>}
      </>
    </Modal>
  );
};

export default withToasts(ChangeDatasourceModal);
